import RequestProcessingModal from '../../../../../components/Modal/RequestProcessingModal/RequestProcessingModal';
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react/jsx-key */
/* eslint-disable no-nested-ternary */
import Button from '@/components/Button/Button';
import Dropdown from '@/components/Dropdown/Dropdown';
import { EditDrawerProcessing } from '@/pages/Franchisee/SharedComponents/EditDrawerProcessing';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { useUpsertBundleUpchargePatches } from '@/services/bundles-upcharges/bundle-upcharges.service';
import { buildUpsertBundleUCRequest } from '@/services/bundles/bundles.service.helper';
import { EditBundleUpchargesDrawerProps } from '@/types/bundles-upcharges.types';
import { BundleUpchargeStore, BundleUpchargeStoreGroup } from '@/types/bundles.types';
import LockOutlined from '@mui/icons-material/LockOutlined';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { t } from 'i18next';
import React, { FC, useState } from 'react';
import { X } from 'react-feather';
import { suppressApiTimeoutToast, enableApiTimeoutToast } from '@/apiClient/apiClient';
import CurrentSettings from './CurrentSettings';
import { useRequestProcessingModal } from '@/hooks/useRequestProcessingModal';
import styles from './EditBundleUpchargesDrawer.module.css';
import { toast } from 'react-hot-toast';

const DROPDOWN_PLACEHOLDER_VALUE = 'placeholder';

export const EditBundleUpchargesDrawer: FC<EditBundleUpchargesDrawerProps> = ({
  isOpen,
  onClose,
  upchargePatches,
  storeGroups,
  stores,
  afterSave,
}) => {
  const [open, setOpen] = useState(isOpen);
  const dropdownKeys = upchargePatches?.filter((up) => !up.isLock).map((up) => up.type);

  const initialDropdownValues: any = {};
  dropdownKeys?.forEach((key) => {
    initialDropdownValues[key] = undefined;
  });
  const [dropdownValues, setDropdownValues] = useState(initialDropdownValues);

  const dropdownNoneOption = [{ label: 'None', value: 'None' }];
  const [tableDataStoreGroup, setTableDataStoreGroup] = useState(storeGroups || []);
  const [tableDataStore, setTableDataStore] = useState(stores || []);
  const [upchargesAreSaved, setUpchargesAreSaved] = useState(false);

  // Suppress timeout toast when drawer opens
  React.useEffect(() => {
    if (isOpen) {
      suppressApiTimeoutToast();
    }
    return () => {
      enableApiTimeoutToast();
    };
  }, [isOpen]);

  const onCloseDrawer = () => {
    setUpchargesAreSaved(false);
    onClose();
  };

  const handleDropdownChange = (key: any, event: any) => {
    setDropdownValues({ ...dropdownValues, [key]: event?.target.value });
  };

  const removeStoreGroup = (string: string) => {
    const newTableData = tableDataStoreGroup.filter(
      (upchargePatch: BundleUpchargeStoreGroup) => upchargePatch.storeGroupId !== string,
    );
    setTableDataStoreGroup(newTableData);
  };

  const removeStore = (string: string) => {
    const newTableData = tableDataStore.filter(
      (upchargePatch: BundleUpchargeStore) => upchargePatch.storeNo !== string,
    );
    setTableDataStore(newTableData);
  };

  const { isModalOpen, modalType, showTimeoutModal, showUpchargeErrorModal, handleCloseAndRefresh } =
    useRequestProcessingModal({
      onRefresh: () => {
        // Refresh to the bundle upcharges page (store assignments table)
        window.location.reload();
      },
    });

  const {
    mutateAsync: upsertBundleUCPatches,
    data: upsertBundleUCPatchesResponse,
    isLoading: isSavingUpcharges = false,
  } = useUpsertBundleUpchargePatches();

  const saveUpcharges = async () => {
    const request = buildUpsertBundleUCRequest(
      tableDataStoreGroup.map((group) => group.storeGroupId),
      tableDataStore.map((store) => store.storeNo),
      upchargePatches,
      dropdownValues,
    );

    try {
      const result = await upsertBundleUCPatches(request);
      setUpchargesAreSaved(true);
      afterSave();
    } catch (error: any) {
      if (error instanceof Error && error.message === 'API Gateway request timed out') {
        showTimeoutModal();
        return;
      }
      showUpchargeErrorModal();
    }
  };

  return (
    <>
      <SwipeableDrawer open={open} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('bundleUpcharges.editDrawer.title')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer} data-testid="close-drawer-button">
              <X data-testid="x-icon-drawer" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.content}>
            {isSavingUpcharges ? (
              <EditDrawerProcessing
                count={tableDataStore.length !== 0 ? tableDataStore.length : tableDataStoreGroup.length}
                entityToUpdate={
                  tableDataStore.length !== 0 ? EntityToUpdate.BUNDLE_UC : EntityToUpdate.BUNDLE_UC_STORE_GROUPS
                }
              />
            ) : upchargesAreSaved ? (
              <EditDrawerSuccessAndFailure
                apiResponse={upsertBundleUCPatchesResponse}
                entityToUpdate={EntityToUpdate.BUNDLE_UC}
              />
            ) : (
              <>
                <div className={styles.editUpcharges}>
                  <strong>{t('bundleUpcharges.editDrawer.newSettings')}</strong>
                  <div className={styles.info} />
                  {upchargePatches?.map((upchargePatch) =>
                    upchargePatch.isLock ? (
                      <div className={styles.lockContainer}>
                        <div className={styles.lockAndDescription}>
                          <LockOutlined
                            sx={{
                              width: '20px',
                              height: '20px',
                              mr: '3px',
                              color: 'red',
                            }}
                          />
                          <span className={styles.dropdownLabel}>{upchargePatch.description}</span>
                        </div>
                        <div className={styles.lockMessaging}>
                          {t('bundleUpcharges.editDrawer.upchargeCurrentlyLocked')}
                        </div>
                      </div>
                    ) : (
                      <Dropdown
                        label={upchargePatch.description}
                        id={`${upchargePatch.type}`}
                        options={dropdownNoneOption.concat(
                          upchargePatch.patchOptions.map((patchOption) => ({
                            label: patchOption.price,
                            value: patchOption.price,
                          })) as any,
                        )}
                        className={styles.dropdown}
                        labelStyle={styles.labelStyle}
                        disablePlaceholderSelection={false}
                        value={dropdownValues[upchargePatch.type]}
                        onChange={(e) => handleDropdownChange(upchargePatch.type, e)}
                      />
                    ),
                  )}
                  <Button
                    onClick={saveUpcharges}
                    type="button"
                    disabled={
                      Object.values(dropdownValues).every(
                        (val: any) => val === DROPDOWN_PLACEHOLDER_VALUE || val === undefined,
                      ) ||
                      (tableDataStore.length === 0 && tableDataStoreGroup.length === 0)
                    }
                    data-testId="editUpchargesSubmitButton"
                  >
                    {t('common.submit')}
                  </Button>
                  <div className={styles.footerContainer}>
                    <div className={styles.footerMessage}>
                      <p>
                        {t('common.submissionPendingRequestDisclaimer')}
                        <span>{t('common.calendar.menuDownloadTimeline')}</span>
                      </p>
                    </div>
                    <div className={styles.footerSubMessage}>
                      <p>{t('common.checkCalendar')}</p>
                    </div>
                  </div>
                </div>
                <CurrentSettings
                  tableDataStoreGroup={tableDataStoreGroup}
                  tableDataStore={tableDataStore}
                  columnsData={upchargePatches}
                  removeStoreGroup={removeStoreGroup}
                  removeStore={removeStore}
                />
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
      {modalType && <RequestProcessingModal isOpen={isModalOpen} onClose={handleCloseAndRefresh} type={modalType} />}
    </>
  );
};
